<template>
  <PortalLayout section="User Collections">
    <div class="mt-4">
      <div v-if="isLoading"><Loading /></div>
      <div v-else>
        <div>
          <AddButton @click="addClick">Add User Collection</AddButton>
        </div>
        <div
          v-for="userCollection in userCollections"
          :key="userCollection.userCollectionId"
          class="bg-gray-100 border-t flex my-2"
          @click="editClick(userCollection)"
        >
          <div
            class="flex-col pl-2 pr-4 py-3 cursor-pointer hover:bg-blue-200 grow h-full"
          >
            <div class="text-lg">{{ userCollection.userCollectionName }}</div>
            <div v-if="userCollection.isStaff" class="ml-2 font-medium text-gray-700">
              <span> Staff </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <EditPopup
      entity-name="User Collection"
      :is-visible="isPopupVisible"
      :is-edit-mode="isEditing"
      :save-error="saveError"
      :delete-enabled="true"
      @close="isPopupVisible = false"
      @save="saveClick"
      @delete="deleteClick"
    >
      <BaseInput
        type="text"
        label="User Collection Name"
        v-model="userCollectionName"
        :error="errors.userCollectionName"
      />
      <BaseCheckbox
        label="Staff Collection"
        v-model="userCollectionIsStaff"
        class="m-2"
      />

      <Listbox v-model="selectedPeople" multiple>
        <div class="relative mt-3">
          <ListboxButton
            class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
          >
            <span class="text-gray-800 text-md">
              {{
                selectedPeople.length > 0
                  ? selectedPeople.length + " people selected"
                  : "Select Users"
              }}
            </span>
            <span
              class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
            >
              <i-heroicons-solid-selector
                class="text-gray-600 w-5 h-5 text-2xl"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>
          <transition
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <ListboxOptions
              class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
              <ListboxOption
                v-for="person in portalPeople"
                :key="person.personId"
                :value="person.personId"
                as="template"
              >
                <li
                  :class="[
                    selectedPeople.includes(person.personId)
                      ? 'border-1 bg-sky-300 border-blue-500'
                      : 'hover:bg-blue-200',
                    'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                  ]"
                >
                  {{ person.firstName }} {{ person.lastName }}
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </EditPopup>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { useBldgStore } from "../../store/bldgStore";
import { usePersonStore } from "../../store/personStore";
import {
  SaveUserCollectionRequest,
  UserCollectionDTO,
} from "../../store/generatedFetchClient";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";
import * as yup from "yup";

const bldgStore = useBldgStore();
const personStore = usePersonStore();

let isLoading = ref(false);
var property = computed(() => bldgStore.property);
var userCollections = computed(() => property.value.userCollections);

onMounted(async () => await loadPeople());
watch(property, loadPeople);

async function loadPeople() {
  console.log("User collection loading data");
  isLoading.value = true;
  if (property?.value?.portalId) {
    await personStore.getForPortal(property?.value?.portalId);
  }
  isLoading.value = false;
}

let portalPeople = computed(() => {
  let peopleList = personStore.people;
  return peopleList
    .filter((p) => !p.dateDisabled)
    .sort((b, a) => {
      if (a.firstName == b.firstName) {
        return (b.lastName ?? "").localeCompare(a.lastName ?? "");
      }
      return (b.firstName ?? "").localeCompare(a.firstName ?? "");
    });
});

// Popup stuff
let isPopupVisible = ref(false);
let isEditing = ref(false);
let saveError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    userCollectionName: yup.string().required().max(50).label("User Collection Name"),
  }),
  initialValues: {
    userCollectionName: "",
  },
});

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let saveUserCollection = {
    userCollectionId: userCollectionId.value,
    userCollectionName: userCollectionName.value,
    isStaff: userCollectionIsStaff.value,
    portalId: property.value.portalId,
    users: selectedPeople.value,
  } as SaveUserCollectionRequest;

  console.log("Saving portal user collection", saveUserCollection);
  saveError.value = "";

  if (property.value && property.value.portalId) {
    let possibleError = await bldgStore.saveUserCollection(saveUserCollection);

    //force people refresh
    await personStore.getForPortal(property?.value?.portalId, true);

    console.log("user collections", property.value.userCollections);

    if (!possibleError) {
      isPopupVisible.value = false;
    } else {
      //handle error
      saveError.value = possibleError;
    }
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

let userCollectionId: Ref<number> = ref(null);
const { value: userCollectionName } = useField<string>("userCollectionName");
const { value: userCollectionIsStaff } = useField<boolean>("userCollectionIsStaff");

var selectedPeople = ref([] as number[]);

let addClick = function () {
  userCollectionId.value = null;
  userCollectionName.value = "";
  userCollectionIsStaff.value = false;
  selectedPeople.value = [];

  saveError.value = "";
  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selectedUserCollection: UserCollectionDTO) {
  userCollectionId.value = selectedUserCollection.userCollectionId;
  userCollectionName.value = selectedUserCollection.userCollectionName;
  userCollectionIsStaff.value = selectedUserCollection.isStaff;
  selectedPeople.value = portalPeople.value
    .filter((p) => p.userCollections.includes(userCollectionId.value))
    .map((p) => p.personId);

  saveError.value = "";
  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  if (isEditing.value && userCollectionId.value > 0) {
    saveError.value = await bldgStore.deleteUserCollection({
      userCollectionId: userCollectionId.value,
      portalId: property.value.portalId,
    });

    if (!saveError.value) {
      await bldgStore.tryGetBuildingData(property.value.siteCode, true);
      isPopupVisible.value = false;
    }
  }
}
</script>
